import ExcelJS from 'exceljs'
import type { CreateCatalogEntryPayload } from '@/api/productsApi'
import { ISBT_FIELDS, type IsbtFieldKey } from '@/lib/isbtCatalog'

// ─── Product catalog import (Excel or CSV) ──────────────────────────────────
//
// One header row, one ISBT 128 product code per row after it. No network or
// React work — callers pass file bytes/text in and get plain rows + errors out.
//
// Only `Product_Code` is required. Every other column is optional and matched
// by name, so a file carrying a subset of the 44 columns imports cleanly and a
// file carrying all of them loses nothing.

/**
 * Header spelling accepted for each field.
 *
 * The source workbook writes `Product_Code`, `Storage_Temperature` and so on,
 * but matching is loose (see `normalizeHeader`), so `Product Code` and
 * `productCode` land on the same field. The canonical spelling below is what
 * the downloadable template emits.
 */
export const CATALOG_IMPORT_HEADERS: string[] = ISBT_FIELDS.map(f =>
  f.label.replace(/ /g, '_'),
)

/** Fields that hold a boolean in the source file (`1` / `0`). */
const BOOLEAN_FIELDS: IsbtFieldKey[] = ['inUse', 'expired']

export type ParsedProductCatalog = {
  entries: CreateCatalogEntryPayload[]
  errors: string[]
}

/**
 * Collapse a header to letters and digits only.
 *
 * `Product_Code`, `Product Code`, `product code` and `productCode` all reduce to
 * `productcode`, so a hand-edited file does not fail on a space.
 */
function normalizeHeader(v: unknown): string {
  return String(v ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

/** Every accepted header, resolved to the field it sets. */
const FIELD_BY_HEADER = new Map<string, IsbtFieldKey>(
  ISBT_FIELDS.flatMap(field => {
    const spellings = [field.label, field.label.replace(/ /g, '_'), field.key]
    return spellings.map(s => [normalizeHeader(s), field.key] as const)
  }),
)

/** `1`/`0`, `true`/`false`, `yes`/`no` — the source file uses the first. */
function toBoolean(raw: string): boolean {
  const v = raw.trim().toLowerCase()
  return v === '1' || v === 'true' || v === 'yes' || v === 'y'
}

/** Shared row-processing: `rows[0]` is the header, everything after is data. */
function rowsToCatalog(rows: string[][]): ParsedProductCatalog {
  const header = rows[0] ?? []
  const columnForField = new Map<IsbtFieldKey, number>()
  header.forEach((cell, colIndex) => {
    const field = FIELD_BY_HEADER.get(normalizeHeader(cell))
    // First wins: a file with the column twice uses the leftmost rather than
    // silently preferring whichever happens to be last.
    if (field && !columnForField.has(field)) columnForField.set(field, colIndex)
  })

  if (!columnForField.has('productDescriptionCode')) {
    return { entries: [], errors: ['Missing required column: Product_Code.'] }
  }

  const entries: CreateCatalogEntryPayload[] = []
  const errors: string[] = []
  const seen = new Set<string>()

  for (let r = 1; r < rows.length; r += 1) {
    const row = rows[r]
    const cell = (field: IsbtFieldKey): string => {
      const col = columnForField.get(field)
      return col === undefined ? '' : (row[col] ?? '').trim()
    }

    const code = cell('productDescriptionCode')
    // A wholly blank row is padding, not an error — spreadsheets are full of it.
    if (!code && [...columnForField.keys()].every(f => !cell(f))) continue
    if (!code) {
      errors.push(`Row ${r + 1}: Product_Code is required.`)
      continue
    }
    // The code is the key the import upserts on, so two rows sharing one would
    // have the later silently overwrite the earlier.
    if (seen.has(code)) {
      errors.push(`Row ${r + 1}: duplicate Product_Code ${code}.`)
      continue
    }
    seen.add(code)

    const entry = { productDescriptionCode: code } as CreateCatalogEntryPayload
    for (const field of columnForField.keys()) {
      if (field === 'productDescriptionCode') continue
      const raw = cell(field)
      if (BOOLEAN_FIELDS.includes(field)) {
        ;(entry as Record<string, unknown>)[field] = toBoolean(raw)
      } else if (field === 'attributeCount') {
        const n = Number(raw)
        ;(entry as Record<string, unknown>)[field] = Number.isFinite(n) && n >= 0 ? n : 0
      } else {
        ;(entry as Record<string, unknown>)[field] = raw
      }
    }
    entries.push(entry)
  }

  return { entries, errors }
}

/**
 * Pick the worksheet to read.
 *
 * The source workbook has eight sheets and the codes are on `Parsed`, not the
 * first one — reading `worksheets[0]` would import the input sheet. A file with
 * no sheet by that name falls through to the first, which is what a hand-made
 * one-sheet export looks like.
 */
function pickWorksheet(wb: ExcelJS.Workbook): ExcelJS.Worksheet | undefined {
  return wb.worksheets.find(ws => ws.name.trim().toLowerCase() === 'parsed') ?? wb.worksheets[0]
}

/**
 * Every row of the workbook's catalog sheet, as strings — or null when the file
 * has no worksheet. Shared with the status upload (`productCodeStatus.ts`),
 * which reads the same exports back.
 */
export async function readWorkbookRows(buffer: ArrayBuffer): Promise<string[][] | null> {
  const wb = new ExcelJS.Workbook()
  await wb.xlsx.load(buffer)
  const ws = pickWorksheet(wb)
  if (!ws) return null

  const rows: string[][] = []
  for (let r = 1; r <= ws.rowCount; r += 1) {
    const row = ws.getRow(r)
    const cells: string[] = []
    // `cellCount` is the last populated cell of THIS row, so a row whose tail
    // is blank yields a short array — callers read missing indices as ''.
    for (let c = 1; c <= row.cellCount; c += 1) cells.push(String(row.getCell(c).value ?? ''))
    rows.push(cells)
  }
  return rows
}

/**
 * Every non-blank record of a CSV file, split into cells.
 *
 * Quote-aware across lines, unlike splitting on newlines first: a quoted cell
 * may hold a line break (a multi-line Note, which the export writes quoted),
 * and splitting the file on it would cut the cell and turn its second line
 * into a row of its own. Cells are trimmed, as `splitCsvLine` does.
 */
export function readCsvRows(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let inQuotes = false
  let blankRecord = true
  const endCell = () => {
    row.push(cell.trim())
    cell = ''
  }
  const endRow = () => {
    endCell()
    if (!blankRecord) rows.push(row)
    row = []
    blankRecord = true
  }
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i]
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cell += '"'
          i += 1
        } else {
          inQuotes = false
        }
      } else {
        cell += c
      }
      continue
    }
    if (c === '"') {
      inQuotes = true
      blankRecord = false
    } else if (c === ',') {
      endCell()
      blankRecord = false
    } else if (c === '\r' || c === '\n') {
      if (c === '\r' && text[i + 1] === '\n') i += 1
      endRow()
    } else {
      cell += c
      blankRecord = false
    }
  }
  if (cell !== '' || row.length > 0) endRow()
  return rows
}

export async function parseProductCatalogWorkbook(
  buffer: ArrayBuffer,
): Promise<ParsedProductCatalog> {
  const rows = await readWorkbookRows(buffer)
  if (!rows) return { entries: [], errors: ['No worksheet found in the file.'] }
  return rowsToCatalog(rows)
}

/** Splits one CSV line, honoring double-quoted fields (with `""` as an escaped quote). */
export function splitCsvLine(line: string): string[] {
  const cells: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i += 1) {
    const c = line[i]
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"'
          i += 1
        } else {
          inQuotes = false
        }
      } else {
        cur += c
      }
    } else if (c === '"') {
      inQuotes = true
    } else if (c === ',') {
      cells.push(cur.trim())
      cur = ''
    } else {
      cur += c
    }
  }
  cells.push(cur.trim())
  return cells
}

export function parseProductCatalogCsv(text: string): ParsedProductCatalog {
  const rows = readCsvRows(text)
  if (rows.length === 0) return { entries: [], errors: ['The file is empty.'] }
  return rowsToCatalog(rows)
}
